'use client';

import { useToast } from '@/contexts/ToastContext';
import { FC, useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { EditableColumn, EditableDataTable } from '@/components/ui';
import { EmailLog } from '../types';

export const EmailLogsTable: FC = () => {
  const [tableData, setTableData] = useState<EmailLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false); // ✅ added
  const [searchQuery, setSearchQuery] = useState('');

  const { addToast } = useToast();

  const fetchLogs = useCallback(
    async (page: number, limit: number, query: string) => {
      try {
        setLoading(true);
        const q = String(query ?? '').trim();
        const hasQuery = q.length > 0;
        const endpoint = hasQuery ? '/email-logs/search' : '/email-logs';
        const params: Record<string, string | number> = { page, limit };
        if (hasQuery) params.email = q; // change key if your API uses ?q

        const res = await api.get(endpoint, { params });
        setTableData(res.data.items || []);
        setTotalCount(res.data.total ?? res.data.count ?? 0);
      } catch (err) {
        console.error('❌ Failed to fetch logs:', err);
        addToast('Failed to load logs', 'error');
      } finally {
        setLoading(false);
      }
    },
    [addToast]
  );

  // ✅ single place where fetching happens
  useEffect(() => {
    fetchLogs(currentPage, pageSize, searchQuery);
  }, [currentPage, pageSize, searchQuery, fetchLogs]);

  // 🔍 MRT global search handler — only updates state
  const handleGlobalSearch = async (query?: string) => {
    const q = String(query ?? '');
    setSearchQuery(q);
    setCurrentPage(1); // reset to first page; effect above will fetch
  };

  const resendEmail = async (email: string) => {
    try {
      setLoading(true);
      await api.get(`/email-logs/re-send`, { params: { email } });
      addToast(`✅ Email re-sent successfully to ${email}`, 'success');
    } catch {
      addToast(`❌ Failed to resend email to ${email}`, 'error');
    } finally {
      setLoading(false);
      // refresh with current state (effect will run because we “touch” state)
      setCurrentPage((p) => p); // no-op to trigger effect if needed
    }
  };

  // ✅ Columns Configuration
  const emailLogColumns: EditableColumn<EmailLog>[] = [
    { key: 'email', header: 'Email', editable: false },
    { key: 'subject', header: 'Subject', editable: false },
    {
      key: 'status',
      header: 'Status',
      editable: false,
      render: (row) => (
        <span
          className={`font-semibold ${row.status === 'SENT'
            ? 'text-green-600'
            : row.status === 'FAILED'
              ? 'text-red-600'
              : 'text-yellow-600'
            }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      editable: false,
      render: (row) => (
        <span className="text-gray-500">
          {new Date(row.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'logsActions',
      header: 'Action',
      editable: false,
      render: (row) => (
        <button
          disabled={row.status !== 'FAILED'}
          onClick={() => resendEmail(row.email)}
          className={`px-3 py-1 text-xs rounded-md border transition-all duration-200 ${row.status === 'FAILED'
            ? 'border-blue-500 text-blue-600 hover:bg-blue-50'
            : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
        >
          Re-send
        </button>
      ),
    },
  ];

  return (
    <EditableDataTable<EmailLog>
      title="Email Logs"
      columns={emailLogColumns}
      data={tableData}
      enableEditing={false}
      enableDelete={false}
      className="mt-4"
      onDataUpdate={setTableData}
      onGlobalSearch={handleGlobalSearch}
      pagination={{
        pageIndex: currentPage - 1,
        pageSize,
        totalCount,
      }}
      onPaginationChange={({ pageIndex, pageSize: nextSize }) => {
        const sizeChanged = nextSize !== pageSize;
        // just update state; effect will do the fetch ONCE
        setPageSize(nextSize);
        setCurrentPage(sizeChanged ? 1 : pageIndex + 1);
      }}
      loading={loading}
      globalFilterValue={searchQuery}
    />

  );
};
